import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipOption, MatChipRemove } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { KeysResults } from "fuzzysort";

import { PageStatus } from 'app/layout/content';
import { AuthorizationContext } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { FuzzySortResultHighlightComponent } from 'app/shared/fuzzysort-helper/fuzzy-sort-result-highlight.component';
import { emptyFuzzySortOnKeys, executeFuzzySortOnKeys, KeysOfUnion } from 'app/shared/fuzzysort-helper/fuzzysort-helper';
import { Store, StorePermission, StoreStatus, Subscription, SubscriptionPermission } from 'app/shared/graph';
import { CopyToClipboardComponent } from 'app/shared/utilities/copy-to-clipboard.component';

import { CreateStoreDialogComponent, CreateStoreDialogResult } from './create-store-dialog.component';
import { SubscriptionsService } from './subscriptions.service';

@Component({
    selector: 'admin-subscription-stores-component',
    templateUrl: './subscription-stores-component.component.html',
    styleUrls: ['./subscription-stores-component.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatIconButton, MatMenuTrigger, MatIcon, MatMenu, MatMenuItem, MatFormFieldModule, MatPrefix, MatInput, FormsModule, MatChipRemove, MatButton, ErrorComponent, MatTable, MatHeaderRow, MatRow, MatColumnDef, MatHeaderCell, MatCell, RouterLink, CopyToClipboardComponent, MatTooltip, DecimalPipe, MatRowDef, MatHeaderRowDef, MatHeaderCellDef, MatCellDef, MatChipListbox, MatChipOption, FuzzySortResultHighlightComponent]
})
export class SubscriptionStoresComponentComponent implements OnInit, OnChanges {
    private readonly dialog = inject(MatDialog);
    private readonly subscriptionsService = inject(SubscriptionsService);


    pageStatus: PageStatus = PageStatus.loading;
    pageError?: Error;

    fuzzySearchKeys: Array<KeysOfUnion<Store>> = ['id', 'label'];
    displayedStores: KeysResults<Store> = emptyFuzzySortOnKeys();
    filter: {
        name?: string;
        showActiveOnly: boolean,
    } = {
        showActiveOnly: true
    };

    @Input()
    subscription: Subscription;
    @Input()
    auth: AuthorizationContext;

    @ViewChild('storesTable', { static: false })
    storesTable: MatTable<any>;

    ngOnInit(): void {
        const savedFilter = localStorage.getItem('subscription-stores-page-filter');
        if (savedFilter) {
            this.filter = { ...this.filter, ...JSON.parse(savedFilter) as any };
        }
        this.updateDisplayedSubscription();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // The parent page is reused when navigating between subscriptions, so refresh the list on input change
        if (changes.subscription && !changes.subscription.firstChange) {
            this.updateDisplayedSubscription();
        }
    }

    openAddStoreDialog() {
        this.dialog.open<CreateStoreDialogComponent, any, CreateStoreDialogResult>(CreateStoreDialogComponent).afterClosed().subscribe(result => {
            if (!result) {
                return;
            }
            this.pageStatus = PageStatus.saving;
            this.subscriptionsService.createStore({
                ...result,
                subscriptionId: this.subscription.id
            }).subscribe({
                next: (createdStore) => {
                    this.subscription.stores = [...(this.subscription.stores ?? []), createdStore];
                    this.pageStatus = PageStatus.loaded;
                    this.updateDisplayedSubscription();
                },
                error: err => {
                    this.pageError = err;
                    this.pageStatus = PageStatus.saveError;
                }
            });
        });

    }

    updateDisplayedSubscription() {
        const visibleStores = (this.subscription.stores ?? [])
            .filter(s => this.isStoreVisible(s));
        this.displayedStores = executeFuzzySortOnKeys(this.filter.name ?? '', visibleStores, this.fuzzySearchKeys);
        this.storesTable?.renderRows();
    }

    toggleShowActiveOnly() {
        this.filter.showActiveOnly = !this.filter.showActiveOnly;
        this.updateFilter();
    }

    public updateFilter() {
        localStorage.setItem('subscription-stores-page-filter', JSON.stringify({ ...this.filter, name: undefined }));
        this.updateDisplayedSubscription();
    }

    private isStoreVisible(s: Store) {
        if (this.filter.showActiveOnly) {
            if (s.status !== StoreStatus.Active) {
                return false;
            }
        }
        return true;

    }

    canAddStore(): boolean {
        return this.auth.hasSubscriptionPermissions(this.subscription.id, SubscriptionPermission.CreateStore);
    }

    canManageStoreMetadata(storeId: string): boolean {
        return this.auth.hasStorePermissions(storeId, this.subscription.id, StorePermission.ManageStoreMetadata);
    }

}
